// ==================== CARD COMPONENT ====================

import type { ReactNode } from 'react';
import { cn } from '../../utils/helpers';

export interface CardProps {
  children: ReactNode;
  className?: string;
  variant?: 'default' | 'highlight' | 'transparent';
  hoverable?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const Card = ({
  children,
  className,
  variant = 'default',
  hoverable = false,
  padding = 'md',
}: CardProps) => {
  const baseStyles = `
    rounded-xl transition-all duration-200
  `;

  const variants = {
    default: `
      bg-(--color-charcoal-black)
      border border-(--color-border-gray)
    `,
    highlight: `
      bg-linear-to-b from-[rgba(230,142,55,0.1)] to-transparent
      border border-(--color-signal-orange)
    `,
    transparent: `
      bg-transparent
      border border-(--color-border-gray)
    `,
  };

  const paddings = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  const hoverStyles = hoverable
    ? 'hover:border-(--color-waveform) hover:-translate-y-1 hover:shadow-lg cursor-pointer'
    : '';

  return (
    <div className={cn(baseStyles, variants[variant], paddings[padding], hoverStyles, className)}>
      {children}
    </div>
  );
};

// Card Header Component
const CardHeader = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div className={cn('border-b border-(--color-border-gray) pb-4 mb-4', className)}>
    {children}
  </div>
);

// Card Title Component
const CardTitle = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <h3 className={cn('text-lg font-semibold text-white font-heading', className)}>
    {children}
  </h3>
);

// Card Description Component
const CardDescription = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <p className={cn('text-sm text-(--color-muted-gray) mt-1', className)}>
    {children}
  </p>
);

// Card Content Component
const CardContent = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => <div className={cn('', className)}>{children}</div>;

// Card Footer Component
const CardFooter = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div className={cn('border-t border-(--color-border-gray) pt-4 mt-4', className)}>
    {children}
  </div>
);

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };

